import type Rollbar from 'rollbar'

const NETWORK_FAILURE_MESSAGES = new Set([
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.'
])

const CROSS_ORIGIN_SCRIPT_ERROR_MESSAGES = new Set(['Script error.', 'Script error'])

/** Leftover Identity Platform sessions refresh STS tokens; flaky networks throw this uncaught. */
const FIREBASE_AUTH_NETWORK_REQUEST_FAILED = 'auth/network-request-failed'

const THIRD_PARTY_ANALYTICS_URL_PATTERNS = [
  /google-analytics\.com/i,
  /googletagmanager\.com/i,
  /analytics\.google\.com/i,
  /google\.com\/ccm\//i,
  /googlesyndication\.com/i,
  /segment\.io/i, // CopilotKit includes @segment/analytics-node, and api.segment.io is CSP-allowlisted
  /featureassets\.org/i, // Statsig endpoint
  /prodregistryv2\.org/i // Statsig endpoint
]

const ROLLBAR_RATE_LIMIT_MESSAGES = [
  'item per minute limit reached, ignoring errors until timeout',
  'maxItems has been hit, ignoring errors until reset.'
]

/** Crawlers execute the dashboard and report uncaught SyntaxErrors with no stack. */
const BOT_USER_AGENT_PATTERN = /PetalBot|Bytespider|GPTBot|Googlebot/i

const isThirdPartyAnalyticsUrl = (url: string): boolean =>
  THIRD_PARTY_ANALYTICS_URL_PATTERNS.some((pattern) => pattern.test(url))

const getPayloadMessage = (payload: Rollbar.Payload): string | undefined => {
  const exceptionMessage = payload.body?.trace?.exception?.message
  if (typeof exceptionMessage === 'string') {
    return exceptionMessage
  }

  const logMessage = payload.body?.message?.body
  if (typeof logMessage === 'string') {
    return logMessage
  }

  return undefined
}

export const isCrossOriginScriptError = (message: string): boolean => CROSS_ORIGIN_SCRIPT_ERROR_MESSAGES.has(message)

export const isFirebaseAuthNetworkRequestFailed = (message: string): boolean =>
  message.includes(FIREBASE_AUTH_NETWORK_REQUEST_FAILED)

export const isRollbarRateLimitMessage = (message: string): boolean =>
  ROLLBAR_RATE_LIMIT_MESSAGES.some((rateLimitMessage) => message.includes(rateLimitMessage))

export const isBotUserAgent = (userAgent: string): boolean => BOT_USER_AGENT_PATTERN.test(userAgent)

const getBrowserUserAgent = (payload: Rollbar.Payload): string | undefined => {
  const browser = payload.client?.javascript?.browser
  return typeof browser === 'string' ? browser : undefined
}

export const isThirdPartyAnalyticsNetworkFailure = (message: string, payload: Rollbar.Payload): boolean => {
  if (!NETWORK_FAILURE_MESSAGES.has(message)) {
    return false
  }

  const telemetry = payload.body?.telemetry
  if (!Array.isArray(telemetry)) {
    return false
  }

  // Rollbar telemetry is a chronological breadcrumb trail (oldest first, newest
  // last) attached to every error, so it can contain unrelated prior requests.
  // Only the most recent network request is the one that triggered this failure;
  // inspecting any entry would let an earlier blocked analytics request suppress
  // an unrelated first-party failure (e.g. api.alvie.io "Failed to fetch").
  for (let index = telemetry.length - 1; index >= 0; index -= 1) {
    const event = telemetry[index]
    if (event?.type !== 'network') {
      continue
    }

    const url = event.body?.url
    if (typeof url !== 'string') {
      continue
    }

    return isThirdPartyAnalyticsUrl(url)
  }

  return false
}

export const rollbarCheckIgnore = (_isUncaught: boolean, _args: unknown[], payload: Rollbar.Payload): boolean => {
  const userAgent = getBrowserUserAgent(payload)
  if (userAgent != null && isBotUserAgent(userAgent)) {
    return true
  }

  const message = getPayloadMessage(payload)
  if (typeof message !== 'string') {
    return false
  }

  if (isRollbarRateLimitMessage(message)) {
    return true
  }

  if (isCrossOriginScriptError(message)) {
    return true
  }

  if (isFirebaseAuthNetworkRequestFailed(message)) {
    return true
  }

  return isThirdPartyAnalyticsNetworkFailure(message, payload)
}

export const ROLLBAR_HOST_SAFE_LIST = ['alvie.io', 'localhost']
